import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import EmptySearchTemplate from '@atom/components/common/workOrderDetail/taskCreate/EmptySearchTemplate';
import EmptyTaskTemplate from '@atom/components/common/workOrderDetail/taskCreate/EmptyTaskTemplate';
import TaskTemplateContent from '@atom/components/common/workOrderDetail/taskCreate/TaskTemplateContent';
import { GET_TASK_TEMPLATES } from '@atom/graph/taskTemplate';
import { Icon, IconButton, Progress, TextField } from '@atom/mui';
import { ArrowBackIcon, CloseIcon, SearchIcon } from '@atom/mui/icons';
import {
  TaskTemplate,
  TaskTemplatesConnection,
} from '@atom/types/taskTemplate';

import classes from './tasks.module.css';

const LIMIT = 25;
const SEARCH_THRESHOLD = 3;
const DEBOUNCE_TIME = 500;

type TaskTemplatePickerProps = {
  onClose: () => void;
  onConfirm: (taskTemplate: TaskTemplate) => void;
  workTemplateId?: string;
  schemaId?: string;
};

const styles = {
  backButton: {
    padding: 0,
  },
  close: {
    cursor: 'pointer',
  },
  progress: {
    height: '100%',
  },
};

const TaskTemplatePicker = ({
  onClose,
  onConfirm,
  workTemplateId,
  schemaId,
}: TaskTemplatePickerProps) => {
  const ref = useRef(null);

  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [total, setTotal] = useState<number>(0);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [searchTaskTemplates, setSearchTaskTemplates] = useState<
    TaskTemplate[]
  >([]);

  const searching: boolean = useMemo(
    () => search.length >= SEARCH_THRESHOLD,
    [search],
  );

  const [getTaskTemplates, { loading: taskTemplatesLoading }] = useLazyQuery(
    GET_TASK_TEMPLATES,
    {
      fetchPolicy: 'network-only',
    },
  );

  const applyTaskTemplatesResult = (
    data: { taskTemplates: TaskTemplatesConnection },
    isSearching: boolean,
  ) => {
    const nextTaskTemplates = R.pathOr(
      [],
      ['taskTemplates', 'taskTemplates'],
      data,
    );

    if (isSearching) {
      setSearchTaskTemplates(nextTaskTemplates);
    } else {
      setTaskTemplates(prev => [...prev, ...nextTaskTemplates]);
      setTotal(data?.taskTemplates?.totalCount || 0);
    }
  };

  const handleGetTaskTemplates = async (nextPage: number) => {
    const { data } = await getTaskTemplates({
      variables: {
        input: {
          sortBy: 'name',
          isAscending: true,
          limit: LIMIT,
          page: nextPage,
          workTemplateId,
          schemaId,
        },
      },
    });

    applyTaskTemplatesResult(data, false);
  };

  useEffect(() => {
    handleGetTaskTemplates(1);
  }, []);

  const clearSearch = async () => {
    setSearch('');
    setPage(1);
    setSearchTaskTemplates([]);
  };

  const debouncedSearch = useCallback(
    debounce(async (name: string) => {
      const { data } = await getTaskTemplates({
        variables: {
          input: {
            sortBy: 'name',
            isAscending: true,
            limit: LIMIT,
            page: 1,
            name,
            workTemplateId,
            schemaId,
          },
        },
      });

      applyTaskTemplatesResult(data, true);
    }, DEBOUNCE_TIME),
    [workTemplateId, schemaId],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);

    if (value.length >= SEARCH_THRESHOLD) {
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
    }
  };

  const handlePageScroll = (nextPage: number) => {
    setPage(nextPage);
    handleGetTaskTemplates(nextPage);
  };

  const handleSelect = (taskTemplate: TaskTemplate) => {
    if (taskTemplate) {
      onConfirm(taskTemplate);
      onClose();
    }
  };

  const taskTemplatesData = searching ? searchTaskTemplates : taskTemplates;

  const showEmptyResults = searching && R.isEmpty(searchTaskTemplates);

  return (
    <div
      className={classes.pickerView}
      data-cy="createWorkModalTaskTemplatePicker"
    >
      <div className={classes.pickerHeader}>
        <IconButton
          onClick={onClose}
          style={styles.backButton}
          data-cy="createWorkModalTaskTemplatePickerBack"
        >
          <Icon icon={ArrowBackIcon} />
        </IconButton>
        <span>Choose a Template</span>
      </div>
      <div className={classes.pickerSearch}>
        <TextField
          value={search}
          onChange={event => handleSearch(event.target.value)}
          placeholder="Search Task Templates"
          data-cy="createWorkModalTaskTemplateSearchInput"
          slotProps={{
            input: {
              startAdornment: <Icon icon={SearchIcon} />,
              endAdornment: search && (
                <Icon
                  icon={CloseIcon}
                  style={styles.close}
                  onClick={clearSearch}
                  data-cy="createWorkModalTaskTemplateSearchClearButton"
                />
              ),
            },
          }}
        />
      </div>
      <div className={classes.pickerBody} ref={ref}>
        {showEmptyResults ? (
          <EmptySearchTemplate taskTemplatesLoading={taskTemplatesLoading} />
        ) : !total ? (
          taskTemplatesLoading ? (
            <Progress size={30} thickness={2} style={styles.progress} />
          ) : (
            <EmptyTaskTemplate />
          )
        ) : (
          <TaskTemplateContent
            outerRef={ref}
            taskTemplates={taskTemplatesData}
            taskTemplatesLoading={taskTemplatesLoading}
            total={total}
            searching={searching}
            page={page}
            selectedTaskTemplate={null}
            setSelectedTaskTemplate={handleSelect}
            handleDelete={() => {}}
            handlePageScroll={handlePageScroll}
            disableDelete
          />
        )}
      </div>
    </div>
  );
};

export default TaskTemplatePicker;
